// ═══════════════════════════════════════════════════════════════════════════
// ARCHIVO: src/utils/dateUtils.js
// Funciones helper para formateo de fechas
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Formatea una fecha a DD/MM/YYYY sin problemas de zona horaria
 * @param {string|Date} fecha - Fecha en formato ISO o Date object
 * @returns {string} Fecha formateada DD/MM/YYYY
 */
export const formatearFecha = (fecha) => {
  if (!fecha) return "";
  
  try {
    // Extraer solo la parte de la fecha (YYYY-MM-DD)
    const fechaStr = String(fecha).substring(0, 10);
    const [year, month, day] = fechaStr.split("-");
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error("Error al formatear fecha:", error);
    return "";
  }
};

/**
 * Formatea una fecha a formato legible: "20 dic 2025"
 * @param {string|Date} fecha - Fecha en formato ISO o Date object
 * @returns {string} Fecha formateada "DD mes YYYY"
 */
// ✅ Versión corregida para dateUtils.js
export const formatearFechaLegible = (fecha) => {
  if (!fecha) return "";
  
  try {
    // Forzamos a que siempre sea string y tomamos solo los primeros 10 caracteres
    const str = String(fecha);
    const fechaLimpia = str.includes('T') ? str.split('T')[0] : str.substring(0, 10);
    
    const [year, month, day] = fechaLimpia.split("-");
    
    const meses = [
      "ene", "feb", "mar", "abr", "may", "jun",
      "jul", "ago", "sep", "oct", "nov", "dic"
    ];
    
    // Usamos los valores directamente del split, sin dejar que JS aplique Timezones
    return `${parseInt(day)} ${meses[parseInt(month) - 1]} ${year}`;
  } catch (error) {
    console.error("Error al formatear fecha legible:", error);
    return "";
  }
};


export const normalizarFecha = (fecha) => {
  if (!fecha) return null;

  const str = String(fecha).trim();

  // Fecha pura YYYY-MM-DD sin componente de hora — usar directamente.
  // No aplicar conversión de timezone: "2026-03-18" debe quedar "2026-03-18".
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

  // Timestamp completo (con hora) — el servidor lo guarda en UTC.
  // Convertir a fecha LOCAL del navegador para que coincida con lo que ve el usuario.
  // Ejemplo: "2026-03-20T03:31:00Z" (UTC) en RD (UTC-4) = 2026-03-19 a las 23:31.
  let isoStr = str.includes(' ') ? str.replace(' ', 'T') : str;
  // Si no tiene indicador de zona horaria, tratarlo como UTC (comportamiento del servidor)
  if (!isoStr.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(isoStr)) {
    isoStr += 'Z';
  }
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return str.substring(0, 10);

  const year  = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day   = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Obtiene la fecha local actual en formato YYYY-MM-DD
 * @returns {string} Fecha actual en formato YYYY-MM-DD
 */
export const obtenerFechaLocal = () => {
  const hoy = new Date();
  const year = hoy.getFullYear();
  const month = String(hoy.getMonth() + 1).padStart(2, "0");
  const day = String(hoy.getDate()).padStart(2, "0");
  
  return `${year}-${month}-${day}`;
};

/**
 * Calcula días transcurridos entre dos fechas
 * @param {string|Date} fechaInicio - Fecha inicial
 * @param {string|Date} fechaFin - Fecha final (opcional, default: hoy)
 * @returns {number} Días transcurridos
 */
export const calcularDiasTranscurridos = (fechaInicio, fechaFin = new Date()) => {
  try {
    const inicio = new Date(String(fechaInicio).substring(0, 10));
    const fin = new Date(String(fechaFin).substring(0, 10));
    
    const diffTime = fin - inicio;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch (error) {
    console.error("Error al calcular días:", error);
    return 0;
  }
};

// src/utils/dateUtils.js

export const formatearHoraRD = (fechaISO) => {
  if (!fechaISO) return "---";

  try {
    const fechaObj = new Date(fechaISO);

    // Verificamos si la fecha es válida
    if (isNaN(fechaObj.getTime())) return "---";

    return fechaObj.toLocaleTimeString('es-DO', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Santo_Domingo' // 👈 Esto es lo que corrige el desfase
    });
  } catch (error) {
    console.error("Error al formatear la hora:", error);
    return "---";
  }
};
const dateUtils = {
  formatearFecha,
  formatearFechaLegible,
  normalizarFecha,
  obtenerFechaLocal,
  calcularDiasTranscurridos,
  formatearHoraRD 
};

export default dateUtils;